import { Popper } from '@material-ui/core';
import React from 'react';
import { Range } from 'slate';

import { useSelector } from '../../../../redux';
import { useSlate } from '../internal/useSlate';
import { getLinkElementAbove } from '../internal/withLinks';
import { AttachmentMenuContainer } from './AttachmentMenuContainer';
import { LinkEditContainter } from './LinkEditContainer';
import { LinkPreviewContainer } from './LinkPreviewContainer';
import { NewLinkContainter } from './NewLinkContainer';
import { useMenuAnchor } from './useMenuAnchor';

export function SelectionMenu({
  selectionPreservingContainer,
  primaryLink,
  onChangePrimaryLink,
}: {
  selectionPreservingContainer: React.RefObject<HTMLElement>;
  primaryLink: string | null | undefined;
  onChangePrimaryLink: (primaryLink: string) => void;
}): React.ReactElement | null {
  const editor = useSlate();
  const selectionMenu = useSelector((state) => state.editor.selectionMenu);
  const menuAnchor = useMenuAnchor({ editor, selectionPreservingContainer });

  if (menuAnchor == null) {
    return null;
  }

  const { selection, anchorEl, elementWithSelectionMenu } = menuAnchor;

  const content = (): React.ReactElement | null => {
    if (selectionMenu?.type === 'new_link' && Range.equals(selection, selectionMenu.selection)) {
      return <NewLinkContainter editor={editor} selection={selection} />;
    }

    if (selectionMenu?.type === 'edit_link' && Range.equals(selection, selectionMenu.selection)) {
      const linkEditState = getLinkElementAbove({ editor, selection });
      if (linkEditState != null) {
        const { linkElementPath, text, url } = linkEditState;
        return (
          <LinkEditContainter editor={editor} initialText={text} initialUrl={url} linkElementPath={linkElementPath} />
        );
      }
    }

    if (elementWithSelectionMenu != null) {
      const { node, path } = elementWithSelectionMenu;

      switch (node.type) {
        case 'link': {
          return (
            <LinkPreviewContainer
              isPrimaryLink={primaryLink === node.url}
              onChangePrimaryLink={onChangePrimaryLink}
              editor={editor}
              selection={selection}
              node={node}
              path={path}
            />
          );
        }
        case 'image':
        case 'expanded_link': {
          return (
            <AttachmentMenuContainer
              editor={editor}
              node={node}
              path={path}
              isPrimaryLink={primaryLink === node.url}
              onChangePrimaryLink={onChangePrimaryLink}
            />
          );
        }
      }
    }

    return null;
  };

  if (content != null) {
    return (
      <Popper id="link-more-options-popover" open anchorEl={anchorEl} placement="bottom" transition disablePortal>
        {content}
      </Popper>
    );
  } else {
    return null;
  }
}
